import { UUID } from "crypto";

import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { EditorAnnotationType, useAnnotationStore } from "@perceptron/core/annotation-editor";
import { MediaKind } from "@perceptron/utils/types";
import { JSONContent } from "@tiptap/react";

import { MediaFactory } from "@demo/libs/media-factory";
import { MediaStoreAdapter, getMediaStoreAdapter } from "@demo/store/adapters/media";
import { MediaData, Role } from "@demo/types/base";
import {
  type DocumentInputContentItem,
  type DocumentParagraph,
  type DocumentMedia,
  type Image,
} from "@demo/types/documentContent";
import { MediaSet } from "@demo/types/media";
import { selectMediaByIds } from "@demo/utils/media-order";

/**
 * Processes document content and extracts structured data
 */
export class DocumentContentProcessor {
  private mediaStoreAdapter: MediaStoreAdapter;

  constructor() {
    this.mediaStoreAdapter = getMediaStoreAdapter();
  }

  /**
   * Convert JSON content to document content array for API
   */
  processJsonToContent(json: JSONContent, _role: Role): DocumentInputContentItem[] {
    if (!json.content) return [];

    const out: DocumentInputContentItem[] = [];

    // Walk in document order so media nested in list items, quotes, and other
    // block wrappers stays in the same position as its prompt text.
    (json.content || []).forEach((node) => this.processBlockNode(node, out));

    return out;
  }

  /**
   * Extract all media from document content in order
   */
  extractMediaFromJson(json: JSONContent): MediaData[] {
    if (!json.content) return [];

    const media: MediaData[] = [];

    const processNode = (node: JSONContent) => {
      if (node.type === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER && node.attrs?.id) {
        const mediaSet = this.findMediaSet(node.attrs.id);
        if (mediaSet) {
          media.push(...selectMediaByIds(mediaSet.media, node.attrs.mediaIds));
        }
      }

      // Recursively process child nodes
      if (node.content && Array.isArray(node.content)) {
        node.content.forEach(processNode);
      }
    };

    (json.content || []).forEach(processNode);
    return media;
  }

  /**
   * Extract media from multiple documents in order
   */
  extractMediaFromDocuments(documents: JSONContent[]): MediaData[] {
    return documents.flatMap((doc) => this.extractMediaFromJson(doc));
  }

  /**
   * Extract distinct media kinds from document content
   * Returns a Set of MediaKind values present in the document
   */
  extractDistinctMediaKinds(json: JSONContent): Set<MediaKind> {
    if (!json.content) return new Set();

    const mediaKinds = new Set<MediaKind>();

    const processNode = (node: JSONContent) => {
      if (node.type === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER && node.attrs?.id) {
        const mediaSet = this.findMediaSet(node.attrs.id);
        if (mediaSet) {
          // Detect kind for each media item
          selectMediaByIds(mediaSet.media, node.attrs.mediaIds).forEach((item) => {
            mediaKinds.add(MediaFactory.detectMediaKind(item));
          });
        }
      }

      // Recursively process child nodes
      if (node.content && Array.isArray(node.content)) {
        node.content.forEach(processNode);
      }
    };

    (json.content || []).forEach(processNode);
    return mediaKinds;
  }

  private processParagraphNode(node: JSONContent, out: DocumentInputContentItem[]) {
    if (!node.content || node.content.length === 0) {
      // Preserve empty paragraphs as empty text content
      const paragraph: DocumentParagraph = { type: "text", value: "" };
      out.push({ type: "paragraph", value: paragraph });
      return;
    }

    // Process each child node in the paragraph
    let currentText = "";

    node.content.forEach((childNode) => {
      if (childNode.type === "text" && childNode.text) {
        // Accumulate text
        currentText += childNode.text;
      } else if (childNode.type === "hardBreak") {
        // Preserve Shift+Enter line breaks — skipping them would glue adjacent
        // lines together in the model request.
        currentText += "\n";
      } else if (childNode.type === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER && childNode.attrs?.id) {
        // Flush any accumulated text first
        if (currentText) {
          const paragraph: DocumentParagraph = { type: "text", value: currentText };
          out.push({ type: "paragraph", value: paragraph });
          currentText = "";
        }

        // Process media container
        this.processMediaSetContainer(childNode, out);
      }
    });

    // Flush any remaining text
    if (currentText) {
      const paragraph: DocumentParagraph = { type: "text", value: currentText };
      out.push({ type: "paragraph", value: paragraph });
    }
  }

  private processBlockNode(node: JSONContent, out: DocumentInputContentItem[]) {
    const hasInlineContent = node.content?.some((child) => child.type === "text" || child.type === "hardBreak");
    if (node.type === "paragraph" || node.type === "heading" || node.type === "codeBlock" || hasInlineContent) {
      this.processParagraphNode(node, out);
      return;
    }
    if (node.type === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER && node.attrs?.id) {
      this.processMediaSetContainer(node, out);
      return;
    }

    node.content?.forEach((child) => this.processBlockNode(child, out));
  }

  private processMediaSetContainer(node: JSONContent, out: DocumentInputContentItem[]) {
    if (!node.attrs?.id) return;

    const setId = node.attrs.id;
    const mediaSet = this.findMediaSet(setId);

    if (!mediaSet || mediaSet.media.length === 0) return;

    // A node's IDs are the authoritative asset list as well as its order.
    // Keep legacy documents with no IDs working in store order.
    const orderedMedia = selectMediaByIds(mediaSet.media, node.attrs.mediaIds);

    // Process each media item based on its detected type
    const mediaArray: DocumentMedia[] = orderedMedia.map((item) => {
      const itemKind = MediaFactory.detectMediaKind(item);

      if (itemKind === MediaKind.Image) {
        // Images include annotations
        const docImage = this.constructDocumentImage(setId, item);
        return {
          type: "image",
          value: docImage,
        } as DocumentMedia;
      } else {
        // Videos don't have annotations
        return {
          type: "video",
          value: {
            uuid: item.key as UUID,
            extension: item.extension,
          },
        } as DocumentMedia;
      }
    });

    if (mediaArray.length > 0) {
      out.push({ type: "media", value: mediaArray });
    }
  }

  private findMediaSet(setId: string): MediaSet | undefined {
    const { getUserMediaSet, getSystemMediaSet } = this.mediaStoreAdapter;

    // Search user media sets first
    const userMediaSets = getUserMediaSet();
    const systemMediaSets = getSystemMediaSet();

    const userSet = userMediaSets.find((set) => set.id === setId);
    if (userSet) return userSet;

    // Only search system media sets if not found in user sets
    return systemMediaSets.find((set) => set.id === setId);
  }

  private constructDocumentImage(mediaSetId: string, imageData: MediaData): Image {
    // Get annotations from the annotationStore
    const annotations = useAnnotationStore.getState().getAnnotations(mediaSetId, imageData.key);

    const points = annotations
      .filter((ann) => ann.type === EditorAnnotationType.Point)
      .map((ann) => ({
        x: ann.x,
        y: ann.y,
        label: ann.label,
      }));

    const boundingBoxes = annotations
      .filter((ann) => ann.type === EditorAnnotationType.Box)
      .map((ann) => ({
        x: ann.x,
        y: ann.y,
        width: ann.width || 0,
        height: ann.height || 0,
        label: ann.label,
      }));

    return {
      uuid: imageData.key as UUID,
      extension: imageData.extension,
      annotations: {
        points,
        boundingBoxes,
      },
    };
  }
}
